import { getMarketPosition as getMarketPositionMorpho, } from "@bracket/morpho";
import type { EpochSeconds, EthereumAddress, } from "@bracket/types";
import type { LoadedVault, MorphoMarketPositionValues, } from "@types";
import { keccak256, toHex, } from "viem";
import { getMorphoIdFromURL, } from "./getMorphoIdFromURL";
import { isMorphoDisabledVault, } from "./isDisabledVault";

export declare type GetMarketPositionProps = {
	wallet: { address: EthereumAddress; };
	vault: LoadedVault;
	force?: boolean;
};

export declare type GetMarketPositionsProps = {
	wallet: { address: EthereumAddress; };
	vaults: LoadedVault[];
	force?: boolean;
};

export declare type GetMarketPositionResult = Awaited<ReturnType<typeof getMarketPositionMorpho>>;

export const getMarketPosition = async ({
	wallet,
	vault,
	force = false,
}: GetMarketPositionProps): Promise<MorphoMarketPositionValues|null> => {

	if(isMorphoDisabledVault(vault)) return null;

	const { hostname, search, } = globalThis.location;

	// 0x6CF2dd3b2323782Bd043b3DEeD8df4EFf706208b

	const address = (
		hostname !== "app.bracket.fi"
			? search.slice(1).split("&").reduce((acc, v) => !acc && v.split("=")[0] === "morpho"
				? v.split("=")[1]
				: acc, null as null|string) || wallet.address
		: wallet.address
	) as EthereumAddress;

	if(!address) {
		throw new Error("vaults:: getMarketPosition - wallet address not set");
	}

	const marketUniqueKey = getMorphoIdFromURL(vault.morpho_url);
	if(!marketUniqueKey) return null;
	const cached = force ? null : await getCached(marketUniqueKey, address);
	if(cached) return cached;

	setFetching(marketUniqueKey, address);
	const data = await getMarketPositionMorpho({ userAddress: address, marketUniqueKey, symbol: vault.symbol, });

	if(!data) {
		if(data === null) return setCached(data, marketUniqueKey, address);
		throw new Error("vaults:: getMarketPosition::getMarketPositionMorpho - data is null");
	}

	const result = toMorphoMarketPositionValues(data, vault);
	const stored = setCached(result, marketUniqueKey, address);
	return stored;

}


const toMorphoMarketPositionValues = ( data: GetMarketPositionResult, vault: LoadedVault ): MorphoMarketPositionValues => {

	if(!data) throw new Error("vaults:: getMarketPosition::toMorphoMarketPositionValues - data is required");

	const {
		marketPosition: {
			healthFactor,
			priceVariationToLiquidationPrice,
			state: {
				borrowAssets:morphoBorrowAssets,
				borrowAssetsUsd:morphoBorrowAssetsUsd,
				collateral:morphoCollateral,
				collateralUsd:morphoCollateralUsd,
				timestamp,
			}
		},
		marketById: {
			state: {
				avgBorrowApy,
			}
		}
	} = data;
	
	const { apy:vaultApy, } = vault.meta;
	const { current_value, } = vault.exposure;

	const morphoBorrowApy = avgBorrowApy * 100;

	morphoCollateral;
	morphoBorrowAssets;

	const morphoEquity = morphoCollateral - morphoBorrowAssets;
	const morphoEquityUsd = morphoCollateralUsd - morphoBorrowAssetsUsd;

	const vaultEarn = "-1";
	const vaultEarnUsd = current_value.usd * (vaultApy / 100);

	const borrowCost = "-1";
	const borrowCostUsd = morphoCollateralUsd * (morphoBorrowApy / 100);

	const earnings = "-1";
	const earningsUsd = (current_value.usd * (vaultApy / 100)); // - (morphoBorrowAssetsUsd * (morphoBorrowApy / 100));

	const equity = "-1";
	const equityUsd = current_value.usd - morphoBorrowAssetsUsd;

	const effectiveYieldPerc = ( (current_value.usd * (vaultApy / 100) - (morphoBorrowAssetsUsd * (morphoBorrowApy / 100))) / equityUsd ) * 100;
	const effectiveYield = "-1";
	const effectiveYieldUsd = -1;

	const leverage = "-1";
	const leverageUsd = -1;
	const leveragePerc = current_value.usd / equityUsd;

	const healthFactorPerc = healthFactor !== null && healthFactor !== undefined ? healthFactor : null;
	const healthFactorValue = healthFactorPerc !== null ? { asset: BigInt("-1").toString(), usd: -1, perc: healthFactorPerc, } : null;

	const priceVariationToLiquidationPricePerc = priceVariationToLiquidationPrice !== null && priceVariationToLiquidationPrice !== undefined ? priceVariationToLiquidationPrice : null;
	const priceVariationToLiquidationPriceValue = priceVariationToLiquidationPricePerc !== null ? { asset: BigInt("-1").toString(), usd: -1, perc: priceVariationToLiquidationPricePerc, } : null;

	return {
		morphoApy: { asset: BigInt("-1").toString(), usd: -1, perc: morphoBorrowApy, },
		morphoBorrow: { asset: BigInt(morphoBorrowAssets).toString(), usd:morphoBorrowAssetsUsd },
		morphoBorrowApy: { perc: morphoBorrowApy, },
		morphoCollateral: { asset: BigInt(morphoCollateral).toString(), usd: morphoCollateralUsd, },
		morphoEquity: { asset: BigInt(morphoEquity).toString(), usd:morphoEquityUsd },
		vaultEarn: { asset: BigInt(vaultEarn).toString(), usd:vaultEarnUsd },
		borrowCost: { asset: BigInt(borrowCost).toString(), usd:borrowCostUsd },
		earnings: { asset: BigInt(earnings).toString(), usd:earningsUsd },
		equity: { asset: BigInt(equity).toString(), usd:equityUsd },
		effectiveYield: { asset: BigInt(effectiveYield).toString(), usd:effectiveYieldUsd, perc: isNaN(effectiveYieldPerc) ? 0 : effectiveYieldPerc },
		leverage: { asset: BigInt(leverage).toString(), usd:leverageUsd, perc:leveragePerc },
		maxLeverage: { asset: BigInt(leverage).toString(), usd:leverageUsd, perc:leveragePerc },
		healthFactor: healthFactorValue,
		priceVariationToLiquidationPrice: priceVariationToLiquidationPriceValue,
		timestamp,
	};

}

declare type SetCachedOptions = {
	ttl: EpochSeconds;
};

const TTL = 60 * 1000;

const toKey = ( pk: string, address: EthereumAddress, ) => {
	return keccak256(toHex(`${pk}:${address}`));
}

const setCached = ( value: MorphoMarketPositionValues | null, pk: string, address: EthereumAddress, options = { ttl: TTL, }): MorphoMarketPositionValues|null => {
	if(!value && value !== null) throw new Error("vaults:: getMarketPosition::setCached - value is required");
	if(!pk) throw new Error("vaults:: getMarketPosition::setCached - pk is required");
	if(!address) throw new Error("vaults:: getMarketPosition::setCached - address is required");
	const { ttl, } = options;
	const key = toKey(pk, address);
	cached.set(key, { value, since: Date.now(), fetching: false, ttl, });
	const cachedValue = cached.get(key);
	if(cachedValue) return cachedValue.value;
	throw new Error("vaults:: getMarketPosition::setCached - cached value not found");
}

const getCached = async ( pk: string, address: EthereumAddress, ): Promise<MorphoMarketPositionValues|null> => {

	if(!pk) throw new Error("vaults:: getMarketPosition::getCached - pk is required");
	if(!address) throw new Error("vaults:: getMarketPosition::getCached - address is required");

	const key = toKey(pk, address);

	const stored = cached.get(key);
	if(!stored) return null;

	const { fetching, value, since, ttl, } = stored;
	if(fetching) {
		const result = await awaitCached(pk, address);
		return result;
	}

	const expired = Date.now() - ttl > since;
	if(expired) {
		cached.delete(key);
		return null;
	}
	return value;
}

const setFetching = ( pk: string, address: EthereumAddress, ) => {
	const key = toKey(pk, address);
	const existing = cached.get(key);
	cached.set(key, existing ? { ...existing, fetching: true } : { fetching: true, value: null, since: 0, ttl: TTL, });
}

declare type AwaitCachedOptions = {
	timeout?: number;
};

declare type AwaitCachedDefaultTypes = {
	timeout: number;
};

const AwaitCachedDefaults: AwaitCachedDefaultTypes = {
	timeout: 5000,
};

const awaitCached = ( pk: string, address: EthereumAddress, options: AwaitCachedOptions = AwaitCachedDefaults): Promise<MorphoMarketPositionValues|null> => {
	return new Promise((resolve, reject) => {
		const { timeout }  = { ...AwaitCachedDefaults, ...options, };
		const to = setTimeout(() => {
			reject(new Error("awaitCached timed out"));
		}, timeout);
		const inte = setInterval(() => {
			const item = cached.get(toKey(pk, address));
			if(item) {
				const { fetching, value, since, ttl, } = item;
				if(!fetching && (value || value === null)) {
					clearTimeout(to);
					clearInterval(inte);
					resolve(value);	
				}
			}
		}, 25);

	});
}

const cached = new Map<string, { value:MorphoMarketPositionValues|null; since: number; fetching: boolean; ttl: EpochSeconds; }>();
